import { Grid, Typography, IconButton } from '@material-ui/core';
import RemoveIcon from '@material-ui/icons/RemoveCircleSharp';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { DimensionObjectType } from '~/types';
import {
  ResourceUsersDropdown,
  ProjectTeamMemberDropdown,
  RoleDropdown,
  ResourceRoleDropdown
} from '~/modules/tasks/components';
import { useIsRateTableDimensionEnabled } from '~/modules/rateCard/advancedRateCard/components/hooks';
import AllocateResourceTimeField from './AllocateResourceTimeField';
import EstimatedHoursEditor from './EstimatedHoursEditor';
import useTaskResourceEstimateChangeHandlers from './useTaskResourceEstimateChangeHandlers';

const useStyles = makeStyles(theme => ({
  cell: {
    borderRight: `1px solid ${theme.palette.table.border}`
  },
  errorCell: {
    border: `1px solid ${theme.palette.error.main}`
  },
  removeIconCell: {
    color: theme.palette.text.secondary
  },
  estimatedHours: {
    fontWeight: theme.typography.fontWeightBold
  },
  estimated: {
    height: '100%'
  },
  enterDates: {
    color: theme.palette.text.secondary,
    textTransform: 'capitalize',
    height: '100%'
  },
  removeIcon: {
    color: theme.palette.text.secondary
  },
  allocateResourceTimeCell: {
    display: 'flex'
  },
  container: {
    marginBottom: 0,
    marginTop: 0,
    borderBottom: `1px solid ${theme.palette.table.border}`
  }
}));

const useInputResourceUserDropdownStyles = makeStyles(() => ({
  popper: {
    minWidth: 'min-content',
    whiteSpace: 'nowrap'
  }
}));

const PAGE_SIZE = 200;

export const TaskResourceEstimateRow = ({
  resourceEstimate,
  onResourceEstimateChange,
  setFieldValue,
  projectSlug,
  projectId,
  projectCurrency,
  selectedResourceIds,
  error,
  styleProps: { gridItemStyle, removeColumnStyle },
  canViewCost,
  values,
  isFullVersionEnabled,
  getMatchingRowElementRef,
  autoFocusRoleDropdown
}) => {
  const classes = useStyles();
  const inputResourceUserDropdownClasses = useInputResourceUserDropdownStyles();
  const { formatMessage } = useIntl();
  const rolesQueryVariablesStorageKey = 'ACTIVE-ROLES-QUERY-VARIABLES';

  const {
    loading: rateTableDimensionLoading,
    isDimensionEnabled: isRoleRateTableDimensionEnabled
  } = useIsRateTableDimensionEnabled({
    projectId,
    dimensionType: DimensionObjectType.ProjectRole
  });

  const {
    onRemove,
    onEstimatedHoursChange,
    onRoleChange,
    onResourceChange,
    onAllocateResourceTimeChange
  } = useTaskResourceEstimateChangeHandlers({
    onResourceEstimateChange,
    setFieldValue,
    resourceEstimate,
    rolesQueryVariablesStorageKey
  });

  const onAddRoleDialogCancelButtonClick = useCallback(() => {
    onRoleChange(null);
  }, [onRoleChange]);

  const resourceDropdownLabel = formatMessage({
    id: 'taskResourceAssignments.selectAResource'
  });

  const {
    rowId,
    role,
    resource,
    estimatedHours,
    isCreateAllocationEnabled
  } = resourceEstimate;
  const { startDate, endDate } = values;

  const roleDropdownInputRef = useMemo(
    () => getMatchingRowElementRef?.(rowId),
    [rowId, getMatchingRowElementRef]
  );

  return (
    <Grid key={rowId} container className={classes.container}>
      <Grid
        item
        style={gridItemStyle}
        className={error ? classes.errorCell : classes.cell}
      >
        {rateTableDimensionLoading || isRoleRateTableDimensionEnabled ? (
          <ResourceRoleDropdown
            value={role || null}
            onChange={onRoleChange}
            variant="standard"
            placeholder={formatMessage({
              id: 'taskResourceAssignments.selectARole'
            })}
            onAddRoleDialogCancelButtonClick={onAddRoleDialogCancelButtonClick}
            projectId={projectId}
            error={error}
            rateTableDimensionLoading={rateTableDimensionLoading}
            inputRef={roleDropdownInputRef}
            autoFocus={autoFocusRoleDropdown}
          />
        ) : (
          <RoleDropdown
            value={role || null}
            onChange={onRoleChange}
            variant="standard"
            placeholder={formatMessage({
              id: 'taskResourceAssignments.selectARole'
            })}
            targetCurrencyId={projectCurrency?.id}
            pageSize={PAGE_SIZE}
            queryVariablesStorageKey={rolesQueryVariablesStorageKey}
            onAddRoleDialogCancelButtonClick={onAddRoleDialogCancelButtonClick}
            inputRef={roleDropdownInputRef}
            autoFocus={autoFocusRoleDropdown}
          />
        )}
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Grid>
      <>
        <Grid item style={gridItemStyle} className={classes.cell}>
          <div className={classes.estimated}>
            <EstimatedHoursEditor
              onEstimatedHoursChange={onEstimatedHoursChange}
              resourceEstimate={resourceEstimate}
              canViewCost={canViewCost}
            />
          </div>
        </Grid>
        {isFullVersionEnabled && (
          <Grid
            item
            style={gridItemStyle}
            className={classNames(
              classes.cell,
              classes.allocateResourceTimeCell
            )}
          >
            <AllocateResourceTimeField
              startDate={startDate}
              endDate={endDate}
              estimatedHours={estimatedHours}
              resource={resource}
              rowId={rowId}
              isCreateAllocationEnabled={isCreateAllocationEnabled}
              onAllocateResourceTimeChange={onAllocateResourceTimeChange}
            />
          </Grid>
        )}
      </>
      <Grid item style={gridItemStyle} className={classes.cell}>
        {isFullVersionEnabled ? (
          <ResourceUsersDropdown
            assignedRole={role}
            onResourceChange={onResourceChange}
            projectId={projectId}
            value={resource}
            placeholder={resourceDropdownLabel}
            ariaLabel={resourceDropdownLabel}
            variant="standard"
            startDate={startDate}
            endDate={endDate}
            initialEstimatedHours={estimatedHours}
            hideAvailability={
              !startDate ||
              !endDate ||
              !estimatedHours ||
              !isCreateAllocationEnabled
            }
            selectedResourceIds={selectedResourceIds}
            inputClasses={inputResourceUserDropdownClasses}
            includeCostRate
            targetCurrencyId={projectCurrency.id}
          />
        ) : (
          <ProjectTeamMemberDropdown
            projectId={projectId}
            assignedRole={role}
            onResourceChange={onResourceChange}
            projectSlug={projectSlug}
            value={resource}
            selectedResourceIds={selectedResourceIds}
            placeholder={resourceDropdownLabel}
            includeUserRate={canViewCost}
          />
        )}
      </Grid>
      <Grid item className={classes.removeIconCell} style={removeColumnStyle}>
        <IconButton
          className={classes.removeIcon}
          aria-label={formatMessage({
            id: 'button.delete'
          })}
          onClick={onRemove}
          data-qe-id="RemoveTaskResourceEstimateButton"
        >
          <RemoveIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
};

TaskResourceEstimateRow.propTypes = {
  error: PropTypes.string,
  resourceEstimate: PropTypes.object.isRequired,
  onResourceEstimateChange: PropTypes.func,
  setFieldValue: PropTypes.func,
  projectId: PropTypes.string,
  projectSlug: PropTypes.string,
  projectCurrency: PropTypes.object,
  selectedResourceIds: PropTypes.array,
  styleProps: PropTypes.object,
  canViewCost: PropTypes.bool,
  values: PropTypes.object,
  isFullVersionEnabled: PropTypes.bool,
  getMatchingRowElementRef: PropTypes.func,
  autoFocusRoleDropdown: PropTypes.bool
};

export default TaskResourceEstimateRow;
